import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';

import { Text, Separator, Button } from '@cuemath/comps';

import { closeModal } from 'actions/modal';
import {
  post as createRelationshipManager,
  invalidateList as invalidateRelationshipManagers,
} from 'actions/relationship-manager';
import {
  Head,
  Footer,
  Message,
  FormWrapper,
  FieldWrapper,
  ButtonWrapper,
  renderFormField,
  getOptions,
} from 'modules/form';
import {
  FORM_ACTIONS,
  FIELD_TYPES,
  getOnChangeMethod,
  getInputChangeMethod,
  getHeadText,
  relationshipManagerFields,
} from 'constants/form';

class RelationshipManagerModal extends PureComponent {
  static defaultProps = {
    data: {},
  };

  static propTypes = {
    actions: PropTypes.shape({
      createRelationshipManager: PropTypes.func.isRequired,
      invalidateRelationshipManagers: PropTypes.func.isRequired,
    }).isRequired,
    data: PropTypes.object,
    formType: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorText: null,
      message: null,
      relationshipManagerPost: store.post(),
      form: this.getFormDefaults(),
      formFields: this.getFormFields(),
      modRelationshipManager: false,
    };
    relationshipManagerFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  getFormDefaults = () => {
    const { data } = this.props;
    const formDefaults = {};

    relationshipManagerFields.forEach(field => {
      formDefaults[field.name] =
        !!data && data[field.param] ? data[field.param] : field.default;
    });
    return formDefaults;
  };

  getFormFields = () => {
    const { data: { managersList = [] } = {} } = this.props;
    return relationshipManagerFields.map(field => {
      if (field.fieldType === FIELD_TYPES.SELECT)
        field.choices = getOptions({ field: field.name, managersList });
      return field;
    });
  };

  static getDerivedStateFromProps({ relationshipManagers, actions }, state) {
    const { relationshipManagerPost, modRelationshipManager } = state;

    const createRelationshipManager = store.post(
      relationshipManagers,
      relationshipManagerPost.id,
    );
    const relationshipManagerGet = store.getAll(relationshipManagers);
    const newState = {};

    if (createRelationshipManager.resolved && modRelationshipManager) {
      if (createRelationshipManager.resolved.isCreated) {
        newState.relationshipManagerPost = createRelationshipManager;
        newState.relationshipManagerData = createRelationshipManager.resolved.data;
        newState.message = 'Relationship Manager created successfully';

        // Invalidate relationship managers if not already to show updated list
        if (
          relationshipManagerGet &&
          relationshipManagerGet.isLoaded &&
          !relationshipManagerGet.isStale &&
          modRelationshipManager
        )
          actions.invalidateRelationshipManagers();
        actions.closeModal();
      } else if (createRelationshipManager.resolved.isCreatingFailed) {
        newState.relationshipManagerPost = createRelationshipManager;
        const errorObj = store.getError(createRelationshipManager.resolved);
        newState.errorText = errorObj ? errorObj.message : 'Something went wrong';
      }
      newState.modRelationshipManager = false;
    }
    return newState;
  }

  onChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;

    this.setState(() => ({ ...stateChanges }));
  };

  onOptionChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  handlePostAction = () => {
    const {
      relationshipManagerPost: { id, current },
      form,
      formFields,
    } = this.state;
    const { actions } = this.props;

    if (current && current.isCreating) return;
    const data = {};
    const reqFieldsWithError = [];
    formFields.forEach(({ name, param, required: isRequired, label }) => {
      if (param && form[name]) data[param] = form[name];
      else if (param && isRequired && !form[name]) reqFieldsWithError.push(label);
    });

    if (reqFieldsWithError && reqFieldsWithError.length > 0) {
      this.setState({
        errorText: `Please fill out these fields - ${reqFieldsWithError.toString()}`,
      });
      return;
    }
    this.setState({ modRelationshipManager: true }, () => {
      actions.createRelationshipManager(id, data);
    });
  };

  onSubmit = event => {
    event.preventDefault();
    const { formType } = this.props;

    if (formType === FORM_ACTIONS.CREATE) this.handlePostAction();
  };

  renderFormHeader = formType => (
    <Head>
      <Text size="xlarge" align="center">
        {getHeadText(formType, 'Relationship Manager')}
      </Text>
    </Head>
  );

  renderFormFields = () => {
    const { formType } = this.props;
    const { formFields, form } = this.state;

    return (
      <FieldWrapper>
        {formFields.map(field => {
          const disabled =
            field.disabled ||
            formType === FORM_ACTIONS.VIEW ||
            (!field.editable && formType === FORM_ACTIONS.EDIT);
          const required = formType === FORM_ACTIONS.CREATE ? field.required : false;
          const placeholder = disabled ? '' : field.label;

          return renderFormField({
            ...field,
            value: form[field.name],
            onChange: this[getInputChangeMethod(field)],
            required,
            disabled,
            placeholder,
          });
        })}
      </FieldWrapper>
    );
  };

  renderFormFooter = formType => (
    <Footer>
      <ButtonWrapper>
        <Button label="Save" disabled={formType === FORM_ACTIONS.VIEW} />
      </ButtonWrapper>
    </Footer>
  );

  render() {
    const { message, formType } = this.props;
    const { errorText } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <FormWrapper>
          {this.renderFormHeader(formType)}
          {message && <Message>{message}</Message>}
          <Separator transparent height="25" />
          {this.renderFormFields()}
          {errorText && <Message error={true}>{errorText}</Message>}
          <Separator transparent height="50" />
          {this.renderFormFooter(formType)}
          <Separator transparent height="50" />
        </FormWrapper>
      </form>
    );
  }
}

export default connect(
  ({ relationshipManagers }) => ({ relationshipManagers }),
  dispatch => ({
    actions: bindActionCreators(
      {
        createRelationshipManager,
        invalidateRelationshipManagers,
        closeModal,
      },
      dispatch,
    ),
  }),
)(RelationshipManagerModal);
